'use client';

import { useImageUpload } from '@/hooks/useImageUpload';
import { AppImage } from './AppImage';
import { Spinner } from './Spinner';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

interface ImageUploadProps {
  initialImage?: string;
  tag?: string;
  secure?: boolean;
  maxSize?: number;
  shape?: 'circle' | 'square';
  className?: string;
  onChange?: (url: string) => void;
  onError?: (error: string) => void;
  fallbackText?: string;
  reset?: boolean;
}

export function ImageUpload({
  initialImage,
  tag,
  secure = false,
  maxSize,
  shape = 'circle',
  className,
  onChange,
  onError,
  fallbackText,
  reset,
}: ImageUploadProps) {
  const { uploadImage, isUploading, error, previewUrl, resetImage } =
    useImageUpload({
      tag,
      secure,
      maxSize,
      onSuccess: onChange,
      onError,
    });

  useEffect(() => {
    if (reset) {
      resetImage();
    }
  }, [reset, resetImage]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadImage(file);
    }
  };

  const handleClick = () => {
    const fileInput = document.getElementById(
      'image-upload'
    ) as HTMLInputElement;
    if (fileInput && !isUploading) {
      fileInput.click();
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div
          className={cn(
            'relative h-32 w-32 cursor-pointer overflow-hidden border-2 border-dashed',
            error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50',
            'transition-all hover:border-gray-400 hover:bg-gray-100',
            isUploading && 'cursor-not-allowed opacity-75',
            shape === 'circle' ? 'rounded-full' : 'rounded-lg',
            className
          )}
          onClick={handleClick}
        >
          {(previewUrl || initialImage) && !reset ? (
            <AppImage
              src={previewUrl || initialImage || ''}
              alt="Uploaded image"
              gradient
              shape={shape}
              fallbackText={fallbackText || ''}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center">
              <Upload
                className={cn(
                  'h-10 w-10',
                  error ? 'text-red-400' : 'text-gray-400'
                )}
              />
              <span
                className={cn(
                  'mt-1 text-sm',
                  error ? 'text-red-500' : 'text-gray-500'
                )}
              >
                {error ? 'Upload Failed' : 'Upload Photo'}
              </span>
            </div>
          )}
          <input
            id="image-upload"
            type="file"
            className="sr-only"
            accept="image/*"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </div>
        {isUploading && (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
            <Spinner size="md" className="text-white" />
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <p className={cn('text-sm', error ? 'text-red-500' : 'text-gray-500')}>
        {error ? 'Click to retry upload' : 'Click to update image'}
      </p>
    </div>
  );
}
